import { observer } from 'src/utils/mobx-react';
import React from 'react';
import {
    CheckIconWrapper,
    FilterDropdownWrapper,
    getFilterDropdownStyles,
    DropdownOption,
    OptionWrapper,
} from './SearchAndFilters.style';
import {
    DropdownSelect,
    type OptionProps,
    type SingleValue,
    type GroupBase,
} from 'src/components/DropdownWrapper/DropdownWrapper';
import { computed, makeAutoObservable } from 'mobx';

export type FilterValueType = string | number | null;

export type FilterOptionType = {
    value: FilterValueType;
    label: string | JSX.Element;
    disabled?: boolean;
};

class FilterDropdownState {
    public constructor(
        private readonly optionsList: () => Array<FilterOptionType>,
        public selectedValue: () => FilterValueType,
        public readonly onChangeCallback: (selectedValue: FilterValueType) => void
    ) {
        makeAutoObservable(this);
    }

    public onChangeTrigger = (option: SingleValue<FilterOptionType>): void => {
        if (option === null) {
            console.error('DropdownFilter - filter onChange error');
            return;
        }

        const optionID = option.value?.toString() ?? null;
        if (optionID === null) {
            return;
        }
        this.onChangeCallback(optionID);
    };

    @computed public get optionsListToDisplay(): FilterOptionType[] {
        return this.optionsList();
    }

    @computed public get activeFilterElement(): FilterOptionType | null {
        const currentValue = this.selectedValue();
        if (currentValue === null) {
            return null;
        }
        const filtered = this.optionsListToDisplay.filter((f) => f.value === currentValue)[0];
        return filtered ?? null;
    }
}

interface FilterDropdownPropsType {
    optionsList: () => Array<FilterOptionType>;
    selectedValue: () => FilterValueType;
    onChangeCallback: (selectedValue: FilterValueType) => void;
    placeholder?: string;
}

export const FilterDropdown = observer('FilterDropdown', (props: FilterDropdownPropsType) => {
    const { optionsList, selectedValue, onChangeCallback } = props;
    const [selectionState] = React.useState(
        () => new FilterDropdownState(optionsList, selectedValue, onChangeCallback)
    );

    const CustomOption = (
        optionProps: OptionProps<FilterOptionType, false, GroupBase<FilterOptionType>>
    ): JSX.Element => {
        const { data, innerRef, innerProps, isSelected, isFocused } = optionProps;

        if (data.value === null) {
            return <div />;
        }

        return (
            <OptionWrapper
                ref={innerRef}
                {...innerProps}
                isSelected={isSelected}
                isFocused={isFocused}
                role='option'
                aria-selected={isSelected}
                data-test='all-games-provider-dropdown-option'
            >
                <DropdownOption>
                    <span>{data.label}</span>
                    {isSelected ? <CheckIconWrapper /> : null}
                </DropdownOption>
            </OptionWrapper>
        );
    };

    return (
        <FilterDropdownWrapper>
            <DropdownSelect<FilterOptionType, false>
                placeholder={props.placeholder}
                className='custom-dropdown-select'
                isClearable={false}
                isSearchable={false}
                onChange={selectionState.onChangeTrigger}
                options={selectionState.optionsListToDisplay}
                value={selectionState.activeFilterElement}
                components={{
                    Option: CustomOption,
                }}
                customStyles={getFilterDropdownStyles<FilterOptionType, false>(false)}
                getOptionValue={(option) => option.value?.toString() ?? ''}
                getOptionLabel={(option) => (typeof option.label === 'string' ? option.label : '')}
                hasInternalLabel={false}
            />
        </FilterDropdownWrapper>
    );
});
